import { useCallback, useMemo } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authGateway } from "@domain/auth/authApi";
import { authCommands, authStatusQuery } from "@domain/auth/authQueries";
import type { AuthResult, SocialAuthResult } from "@domain/auth/AuthGateway";
import type { EmailLoginCredentials, GoogleCallbackRequest, SocialLoginRequest } from "@domain/auth/auth";

export const useAuthStatus = () => useQuery(authStatusQuery(authGateway));

export const useAuth = () => {
  const queryClient = useQueryClient();
  const commands = useMemo(() => authCommands(authGateway, queryClient), [queryClient]);
  const status = useAuthStatus();
  const isBusy = useIsMutating({ mutationKey: ["auth"] }) > 0;
  const { refetch } = status;

  const emailLogin = useMutation({
    mutationKey: ["auth", "emailLogin"],
    mutationFn: (credentials: EmailLoginCredentials) => commands.loginWithEmail(credentials),
    meta: { inlineError: true },
  });
  const socialLogin = useMutation({
    mutationKey: ["auth", "socialLogin"],
    mutationFn: (request: SocialLoginRequest) => commands.initiateSocialLogin(request),
    meta: { inlineError: true },
  });
  const googleCallback = useMutation({
    mutationKey: ["auth", "googleCallback"],
    mutationFn: (request: GoogleCallbackRequest) => commands.handleGoogleCallback(request),
    meta: { inlineError: true },
  });
  const signOut = useMutation({
    mutationKey: ["auth", "logout"],
    mutationFn: () => commands.logout(),
  });

  const loginWithEmail = emailLogin.mutateAsync as (credentials: EmailLoginCredentials) => Promise<AuthResult>;
  const initiateSocialLogin = socialLogin.mutateAsync as (request: SocialLoginRequest) => Promise<SocialAuthResult>;
  const handleGoogleCallback = googleCallback.mutateAsync as (request: GoogleCallbackRequest) => Promise<AuthResult>;
  const { mutateAsync: signOutAsync } = signOut;
  const logout = useCallback(() => signOutAsync().catch(() => undefined), [signOutAsync]);
  const checkAuthStatus = useCallback(() => refetch(), [refetch]);

  return {
    isAuthenticated: status.data?.authenticated ?? false,
    isLoading: status.isPending || isBusy,
    verificationFailed: status.isError,
    loginWithEmail,
    initiateSocialLogin,
    handleGoogleCallback,
    logout,
    checkAuthStatus,
  };
};
